import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard, Crosshair, ScanLine, ShieldAlert, FileText, Users, SlidersHorizontal,
  Bug, Boxes, ShieldCheck, Building2, Sparkles, Scale,
  KeyRound, BellRing, CalendarClock, TrendingUp, UserCheck,
} from "lucide-react";
import { Filter } from "lucide-react";
import type { Permission } from "@/lib/rbac/permissions";

export interface NavItem {
  title: string;
  /** Account nav: absolute href. Project nav: segment appended to /projects/[id]. */
  href: string;
  icon: LucideIcon;
  permission?: Permission;
  exact?: boolean;
  badge?: string;
  /**
   * 2.854: shown only where the DEPLOYMENT delivers this. `"ai-testing"` is
   * keyed on the served scan-profile menu listing the AI profile
   * (lib/hooks/use-ai-testing-available.ts), fail-open on a failed read.
   */
  requires?: "ai-testing";
}

/**
 * The one filter every nav surface applies (the two sidebars and the command
 * palette): the permission gate as before, AND, for an entry that names one,
 * the deployment's delivery of the feature (2.854). `aiTestingShown` is
 * `useAiTestingShown()`, passed in so this module stays free of hooks.
 */
export function navItemShown(
  item: NavItem,
  can: (p: Permission) => boolean,
  aiTestingShown: boolean,
): boolean {
  if (item.permission && !can(item.permission)) return false;
  if (item.requires === "ai-testing" && !aiTestingShown) return false;
  return true;
}

export interface NavSection {
  label?: string;
  items: NavItem[];
}

/* ─────────────── Account (org) scope ─────────────── */
export const accountNavItems: NavItem[] = [
  { title: "Overview", href: "/overview", icon: LayoutDashboard, exact: true },
  { title: "Projects", href: "/projects", icon: Boxes },
  { title: "Issues", href: "/issues", icon: Bug, permission: "issues:read" },
  // FR-64 (2.744): any member; the platform sums the projects the caller may see.
  { title: "Trends", href: "/trends", icon: TrendingUp },
  { title: "Alerts", href: "/alerts", icon: BellRing, permission: "alerts:read" },
  { title: "API Keys", href: "/api-keys", icon: KeyRound, permission: "api-keys:read" },
  { title: "Risk Weights", href: "/risk-weights", icon: Scale, permission: "issues:read" },
  { title: "Promotion Rule", href: "/promotion-rule", icon: Filter, permission: "issues:read" },
  { title: "Members", href: "/members", icon: Users, permission: "team:read" },
  { title: "Roles & Permissions", href: "/roles", icon: ShieldCheck, permission: "roles:read" },
  // S-709, ADR-106 (2.790): every subject reads its own, so no permission gates it.
  { title: "My Permissions", href: "/my-permissions", icon: UserCheck },
  { title: "AI Penetration Testing", href: "/ai-testing", icon: Sparkles, permission: "settings:manage", requires: "ai-testing" },
  // Console wording ruling (2026-10-08): "Organisation Settings" (/account) is not
  // rendered in navigation; its fields save nothing. The route is unchanged.
];

/* ─────────────── Platform (super-admin) scope — absolute hrefs ─────────────── */
export const platformNavItems: NavItem[] = [
  { title: "Overview", href: "/platform", icon: LayoutDashboard, exact: true },
  { title: "Organisations", href: "/platform/organisations", icon: Building2 },
  // S-1063: the platform's AI settings. In the PLATFORM shell only: no tenant shell renders this list.
  { title: "AI", href: "/platform/ai", icon: Sparkles },
];

/* ─────────────── Project (project) scope — segments under /projects/[id] ─────────────── */
export const projectNavSections: NavSection[] = [
  {
    // FR-64 (2.744): Trends needs only viewer on the project, as the dashboard does.
    items: [
      { title: "Dashboard", href: "/dashboard", icon: LayoutDashboard, exact: true },
      { title: "Trends", href: "/trends", icon: TrendingUp },
    ],
  },
  {
    label: "Scanning",
    items: [
      { title: "Targets", href: "/targets", icon: Crosshair, permission: "targets:read" },
      { title: "Scans", href: "/scans", icon: ScanLine, permission: "scans:read" },
      { title: "Schedules", href: "/schedules", icon: CalendarClock, permission: "schedules:read" },
      { title: "Vulnerabilities", href: "/vulnerabilities", icon: ShieldAlert, permission: "vulnerabilities:read" },
      // IFC-016's list verb addressed at this project. It sits beside the
      // project's own scans and findings because an issue is something the
      // project OWNS (RAD §8.1); the organisation-wide list stays in the
      // account nav, and the two read the same rows through the same store verb.
      { title: "Issues", href: "/issues", icon: Bug, permission: "issues:read" },
      { title: "Reports", href: "/reports", icon: FileText, permission: "reports:read" },
    ],
  },
  {
    label: "Configuration",
    items: [
      { title: "Scan Profiles", href: "/settings/scan-profiles", icon: SlidersHorizontal, permission: "scan-profiles:manage" },
      { title: "AI Penetration Testing", href: "/settings/ai-testing", icon: Sparkles, permission: "ai-testing:toggle", requires: "ai-testing" },
      // Console wording ruling (2026-10-08): "Excluded Hours" (not served) and the
      // project "Settings" page (its fields save nothing) are not rendered in
      // navigation. Their routes are unchanged.
    ],
  },
];
